import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import type {} from '@redux-devtools/extension'
import type { BidOverview, UpcomingDeadline } from '@/lib/schemas'
import { api } from '@/lib/api'

// ===================
// Types
// ===================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type RawOverview = Record<string, any>

/**
 * Normalize API response to handle both old and new overview shapes.
 * Old shape: complianceStats, requirementsStats, evaluationStats, upcomingKeyDates
 * New shape: submissionReadiness, requirementsSummary, evaluationSummary, deliverableBreakdown, etc.
 * TODO: Remove this once backend is deployed with new shape
 */
function normalizeOverview(raw: RawOverview): BidOverview {
  // Already new shape — pass through
  if (raw['submissionReadiness']) {
    return raw as unknown as BidOverview
  }

  // Map old shape → new shape
  const compliance = (raw['complianceStats'] ?? {}) as RawOverview
  const requirements = (raw['requirementsStats'] ?? {}) as RawOverview
  const evaluation = (raw['evaluationStats'] ?? {}) as RawOverview

  const total = (compliance['total'] ?? 0) as number
  const completed = (compliance['completed'] ?? 0) as number

  // Transform old upcomingDeadlines to new UpcomingDeadline shape
  const deadlines: UpcomingDeadline[] = []

  // Old upcomingDeadlines are SubmissionRequirement[] — normalize to UpcomingDeadline shape
  for (const d of ((raw['upcomingDeadlines'] ?? []) as RawOverview[])) {
    // Check if already new shape
    if (d['source'] && d['severity'] && d['daysUntil'] !== undefined) {
      deadlines.push(d as unknown as UpcomingDeadline)
    } else {
      deadlines.push({
        id: (d['id'] ?? crypto.randomUUID()) as string,
        title: (d['title'] ?? 'Deadline') as string,
        date: (d['deadlineDate'] ?? d['date'] ?? '') as string,
        source: 'submission_requirement' as const,
        severity: 'upcoming' as const,
        daysUntil: 0,
      })
    }
  }

  // Old upcomingKeyDates merged in
  for (const kd of ((raw['upcomingKeyDates'] ?? []) as RawOverview[])) {
    deadlines.push({
      id: (kd['id'] ?? crypto.randomUUID()) as string,
      title: (kd['title'] ?? kd['name'] ?? 'Key Date') as string,
      date: (kd['date'] ?? kd['dueDate'] ?? '') as string,
      source: 'key_date' as const,
      severity: 'upcoming' as const,
      daysUntil: 0,
    })
  }

  return {
    // Old shape doesn't have full SubmissionRequirement objects - set to null/empty
    submissionDeadline: null,
    upcomingDeadlines: deadlines,
    lodgementMethod: null,
    requiredForms: [],
    formsTotal: (raw['formsTotal'] ?? 0) as number,
    formsCompleted: (raw['formsCompleted'] ?? 0) as number,
    submissionReadiness: {
      percentComplete: total > 0 ? Math.round((completed / total) * 100) : 0,
      completed,
      total,
      criticalPending: (compliance['criticalPending'] ?? 0) as number,
      highPending: (compliance['highPending'] ?? 0) as number,
      overdueCount: (compliance['overdueCount'] ?? 0) as number,
      inProgress: (compliance['inProgress'] ?? 0) as number,
      blocked: (compliance['blocked'] ?? 0) as number,
      unassignedCritical: 0,
    },
    requirementsSummary: {
      total: (requirements['total'] ?? 0) as number,
      mandatory: (requirements['mandatory'] ?? 0) as number,
      desirable: (requirements['desirable'] ?? 0) as number,
      information: (requirements['information'] ?? 0) as number,
      linkedToCriteria: (requirements['linkedToCriteria'] ?? 0) as number,
      unlinkedToCriteria: (requirements['unlinkedToCriteria'] ?? 0) as number,
    },
    evaluationSummary: {
      total: (evaluation['total'] ?? 0) as number,
      totalWeighting: (evaluation['totalWeighting'] ?? 0) as number,
      withRequirements: (evaluation['withRequirements'] ?? 0) as number,
      withoutRequirements: (evaluation['withoutRequirements'] ?? 0) as number,
    },
    deliverableBreakdown: (raw['deliverableBreakdown'] ?? {}) as Record<string, number>,
    topSourceDocuments: (raw['topSourceDocuments'] ?? []) as BidOverview['topSourceDocuments'],
    topRiskFlags: (raw['topRiskFlags'] ?? []) as BidOverview['topRiskFlags'],
  } satisfies BidOverview
}

interface BidOverviewState {
  // State
  overview: BidOverview | null
  isLoading: boolean
  error: string | null
  currentBidId: string | null

  // Actions - Fetching
  fetchOverview: (bidId: string) => Promise<void>

  // Actions - Utility
  clearError: () => void
  reset: () => void
}

export const useBidOverviewStore = create<BidOverviewState>()(
  devtools(
    (set) => ({
      // Initial state
      overview: null,
      isLoading: false,
      error: null,
      currentBidId: null,

      // Fetch overview for a bid
      fetchOverview: async (bidId: string) => {
        try {
          set({ isLoading: true, error: null, currentBidId: bidId })
          const raw = await api.get<RawOverview>(`/bids/${bidId}/overview`)
          const overview = normalizeOverview(raw)
          set({ overview, isLoading: false })
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch bid overview',
            isLoading: false,
          })
        }
      },

      // Clear error
      clearError: () => {
        set({ error: null })
      },

      // Reset store state
      reset: () => {
        set({
          overview: null,
          isLoading: false,
          error: null,
          currentBidId: null,
        })
      },
    }),
    { name: 'bid-overview-store' }
  )
)
